// src/components/SystemAwareProtectedRoute.js
import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from '../../hooks/useAuth';
import Layout from '../global/Layout/Layout';
import styles from './ProtectedRoute.module.css';

const SYSTEM_TYPES = {
    BASE: 'base',
    LUXURY: 'luxury'
};

const ROLE_BRACKETS = {
    BASE_ONLY: ['Laboratorio', 'Logistica', 'Marketing', 'Comercial'],
    LUXURY_ONLY: ['Comercial'],
    DUAL_ACCESS: ['TI', 'Diretoria (Admin)']
};

const getUserRoleBracket = (role) => {
    if (!role) return null;
    if (ROLE_BRACKETS.BASE_ONLY.includes(role)) return 'BASE_ONLY';
    if (ROLE_BRACKETS.LUXURY_ONLY.includes(role)) return 'LUXURY_ONLY';
    if (ROLE_BRACKETS.DUAL_ACCESS.includes(role)) return 'DUAL_ACCESS';
    return null;
};

const SystemAwareProtectedRoute = () => {
    const { t } = useTranslation();
    const { currentUser, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className={styles.loadingOverlay}>
                <div className={styles.spinnerContainer}>
                    <div className={styles.spinnerRing}></div>
                    <div className={styles.spinnerRing}></div>
                    <div className={styles.spinnerRing}></div>
                    <div className={styles.logoPulse}>{t('app.title')}</div>
                    <p className={styles.loadingMessage}>{t('auth.checking')}</p>
                </div>
            </div>
        );
    }

    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }

    const userRole = currentUser.role;
    const roleBracket = getUserRoleBracket(userRole);
    const pathname = location.pathname;

    // Check if trying to access luxury routes
    const isLuxuryRoute = pathname.startsWith('/tuv');

    // BASE_ONLY users cannot access luxury routes
    if (roleBracket === 'BASE_ONLY' && isLuxuryRoute) {
        return <Navigate to="/dashboard" replace />;
    }

    // LUXURY_ONLY users cannot access base routes (except maybe root redirect)
    if (roleBracket === 'LUXURY_ONLY' && !isLuxuryRoute && pathname !== '/') {
        if (pathname === '/') {
            return <Navigate to="/dashboard" replace />;
        }
        return <Navigate to="/tuv/dashboard" replace />;
    }

    // Handle root path redirect based on role
    if (pathname === '/') {
        if (roleBracket === 'LUXURY_ONLY') {
            return <Navigate to="/tuv/dashboard" replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }

    return <Layout><Outlet /></Layout>;
};

export default SystemAwareProtectedRoute;